import { useState, useEffect } from "react";
import { Pressable, ActivityIndicator, KeyboardAvoidingView, Platform } from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useNavigation } from "expo-router";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { colors, radius, font } from "@/lib/theme";
import ContentWidth from "@/components/ContentWidth";
import { DateInput } from "@/components/DateTimeInputs";
import { useAlert } from "@/hooks/useAlert";
import AlertSheet from "@/components/AlertSheet";

/* ── Styled Components ── */

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */

const Container = styled.View`
  padding: 20px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.white};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  font-size: 15px;
  color: ${colors.ink};
`;

const ReadOnlyInput = styled.View`
  background-color: ${colors.bg};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
`;

const ReadOnlyText = styled.Text`
  font-size: 15px;
  color: ${colors.inkMuted};
`;

const SectionTitle = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-top: 28px;
  margin-bottom: 4px;
`;

const SectionSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  margin-bottom: 8px;
`;

const SaveBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding-vertical: 16px;
  align-items: center;
  margin-top: 32px;
`;

const SaveBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const OptionalHint = styled.Text`
  font-size: 11px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 4px;
`;

const FieldError = styled.Text`
  font-size: 12px;
  color: ${colors.danger};
  font-weight: ${font.semibold};
  margin-top: 4px;
`;

const BottomSpacer = styled.View`
  height: 40px;
`;

/* ── Screen ── */

export default function MemberProfileScreen() {
  const { user } = useAuth();
  const { club, membership } = useClub();
  const navigation = useNavigation();
  const { alertProps, alert } = useAlert();
  const isOnboarding = membership?.profileComplete === false;

  // During onboarding: hide back button and notification bell
  useEffect(() => {
    if (isOnboarding) {
      navigation.setOptions({
        headerTitle: "Complete your profile",
        headerLeft: () => null,
        headerRight: () => null,
      });
    }
  }, [isOnboarding, navigation]);

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [emergencyContactName, setEmergencyContactName] = useState("");
  const [emergencyContactPhone, setEmergencyContactPhone] = useState("");
  const [medicalConditions, setMedicalConditions] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Pre-fill from existing membership data
  useEffect(() => {
    if (membership) {
      setFullName(membership.displayName || "");
      setPhone(membership.phone || "");
      setEmergencyContactName(membership.emergencyContactName || "");
      setEmergencyContactPhone(membership.emergencyContactPhone || "");
      setMedicalConditions(membership.medicalConditions || "");
      setDateOfBirth(membership.dateOfBirth || "");
    }
  }, [membership, user]);

  const handleSave = async () => {
    if (!club || !user || !membership) {
      await alert({
        title: "Still loading",
        message: "Please wait a moment and try again.",
        emoji: "⏳",
      });
      return;
    }

    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = "Full name is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    setSaving(true);
    try {
      await updateDoc(doc(db, "clubs", club.id, "members", user.uid), {
        displayName: fullName.trim(),
        phone: phone.trim(),
        emergencyContactName: emergencyContactName.trim(),
        emergencyContactPhone: emergencyContactPhone.trim(),
        medicalConditions: medicalConditions.trim() || null,
        dateOfBirth: dateOfBirth.trim() || null,
        profileComplete: true,
      });
      // NavigationGate will automatically route to the app once
      // the membership listener picks up profileComplete: true
    } catch (err: any) {
      await alert({
        title: "Error",
        message: err.message || "Failed to save profile.",
        emoji: "❌",
        buttonColor: colors.danger,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
      <ContentWidth>
        <Container>
          <Label>Email</Label>
          <ReadOnlyInput>
            <ReadOnlyText>{user?.email || "—"}</ReadOnlyText>
          </ReadOnlyInput>

          <Label>Full name</Label>
          <Input
            placeholder="Your full name"
            placeholderTextColor={colors.inkMuted}
            value={fullName}
            onChangeText={(v: string) => { setFullName(v); setErrors((e) => ({ ...e, fullName: "" })); }}
            autoCapitalize="words"
            style={errors.fullName ? { borderColor: colors.danger } : {}}
          />
          {errors.fullName ? <FieldError>{errors.fullName}</FieldError> : null}

          <Label>Phone number</Label>
          <Input
            placeholder="07xxx xxxxxx"
            placeholderTextColor={colors.inkMuted}
            value={phone}
            onChangeText={setPhone}
            keyboardType="phone-pad"
          />

          <Label>Date of birth</Label>
          <DateInput value={dateOfBirth} onChange={setDateOfBirth} past />

          <SectionTitle>Emergency Contact</SectionTitle>
          <SectionSub>Who should we contact in an emergency?</SectionSub>

          <Label>Contact name</Label>
          <Input
            placeholder="Emergency contact name"
            placeholderTextColor={colors.inkMuted}
            value={emergencyContactName}
            onChangeText={(v: string) => { setEmergencyContactName(v); setErrors((e) => ({ ...e, emergencyContactName: "" })); }}
            autoCapitalize="words"
            style={errors.emergencyContactName ? { borderColor: colors.danger } : {}}
          />
          {errors.emergencyContactName ? <FieldError>{errors.emergencyContactName}</FieldError> : null}

          <Label>Contact phone</Label>
          <Input
            placeholder="07xxx xxxxxx"
            placeholderTextColor={colors.inkMuted}
            value={emergencyContactPhone}
            onChangeText={(v: string) => { setEmergencyContactPhone(v); setErrors((e) => ({ ...e, emergencyContactPhone: "" })); }}
            keyboardType="phone-pad"
            style={errors.emergencyContactPhone ? { borderColor: colors.danger } : {}}
          />
          {errors.emergencyContactPhone ? <FieldError>{errors.emergencyContactPhone}</FieldError> : null}

          <Label>Medical conditions</Label>
          <Input
            placeholder="Any conditions we should know about"
            placeholderTextColor={colors.inkMuted}
            value={medicalConditions}
            onChangeText={setMedicalConditions}
            multiline
            numberOfLines={3}
            style={{ minHeight: 80, textAlignVertical: "top" }}
          />
          <OptionalHint>Optional</OptionalHint>

          {/* Junior-specific fields (read-only, set by admin) */}
          {membership?.isJunior && (
            <>
              <SectionTitle>Guardian Details</SectionTitle>
              <SectionSub>Set by your club admin.</SectionSub>

              <Label>Guardian name</Label>
              <ReadOnlyInput>
                <ReadOnlyText>
                  {membership.guardianId ? "Assigned" : "Not yet assigned"}
                </ReadOnlyText>
              </ReadOnlyInput>

              <Label>Guardian email</Label>
              <ReadOnlyInput>
                <ReadOnlyText>
                  {membership.guardianEmail || "Not yet assigned"}
                </ReadOnlyText>
              </ReadOnlyInput>
            </>
          )}

          <Pressable
            style={({ pressed }) => pressed ? { opacity: 0.9 } : {}}
            onPress={handleSave}
            disabled={saving}
          >
            <SaveBtnWrap>
              {saving ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <SaveBtnText>Save profile</SaveBtnText>
              )}
            </SaveBtnWrap>
          </Pressable>

          <BottomSpacer />
        </Container>
      </ContentWidth>
      <AlertSheet {...alertProps} />
    </KeyboardAwareScroll>
  );
}
